import './index.scss';
import { Table } from '@umu/design-base';
import { $document } from '@umu/umu-dom';
import { useHover, useInViewport, useScroll, useSelections, useSize } from 'ahooks';
import { If, IfShow, TableSkeleton } from 'common/src/components';
import _get from 'lodash/get';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { PaginationV2 } from '../pagination';
import { CustomScrollBar } from './components';
import {
    buildSkeletonRecordList,
    isCustomAdvancedTableSkeletonRecord,
    wrapAntColumnsForSkeletonRows,
} from './components/tableBodySkeletonCell';
import { getTableConfig, TABLE_SCROLL_CLS_NO_STICKY, TABLE_SCROLL_CLS_STICKY } from './dataFlow';
import { defaultEmptyText, getColumns } from './dataFlow/element';
import type { IPropsTable, ISelectionsCheckInfo } from './interface';

const CustomAdvancedTable = <T extends object>(props: IPropsTable<T>) => {
    const {
        columns: defaultColumns,
        list = [],
        skeletonInfo,
        isLoading = false,
        className = '',
        pageInfo,
        emptyText = defaultEmptyText,
        sticky = false,
        onPageChange,
        tableCls = '',
        checkKey = 'id',
        offsetX = 0,
        hideTopScroll = false,
        scroll = { x: 1200 },
        onCheckChange,
        style = {},
        rowClassName,
        advancedPagination,
        onPageSizeChange,
    } = props;
    const tableScrollCls = tableCls || (sticky ? TABLE_SCROLL_CLS_STICKY : TABLE_SCROLL_CLS_NO_STICKY);
    const { tableConfig, viewsConfig, scrollWidth } = getTableConfig(props);
    // 初始位置状态
    const [tableWidth, setTableWidth] = useState(0);
    const [isShowTable, setIsShowTable] = useState(false);
    /** 是否至少完成过一次非 loading（用于分页等二次请求时保留表头与分页，不再整表 TableSkeleton） */
    const [hasCompletedInitialLoad, setHasCompletedInitialLoad] = useState(false);
    const [columns, setColumns] = useState(defaultColumns);
    const lastStableRowCountRef = useRef(0);
    const tableRef = useRef<HTMLDivElement>(null);
    const scrollTopRef = useRef<HTMLDivElement>(null);
    const scrollBottomRef = useRef<HTMLDivElement>(null);
    const customAdvancedTableRef = useRef<HTMLDivElement>(null);
    const isDragging = useRef(false);
    const size = useSize(tableRef?.current?.querySelector('table'));
    const bodySize = useSize($document.body);
    useEffect(() => {
        if (!isLoading) {
            setHasCompletedInitialLoad(true);
        }
    }, [isLoading]);

    useEffect(() => {
        if (!isLoading) {
            lastStableRowCountRef.current = list.length;
        }
    }, [isLoading, list]);

    const displayList = useMemo(() => {
        if (isLoading && hasCompletedInitialLoad) {
            const fromSkeletonInfo = skeletonInfo && typeof skeletonInfo.count === 'number' ? skeletonInfo.count : 0;
            const rowCount = Math.max(1, lastStableRowCountRef.current, fromSkeletonInfo);
            return buildSkeletonRecordList<T>(rowCount, checkKey);
        }
        return list;
    }, [isLoading, hasCompletedInitialLoad, list, checkKey, skeletonInfo?.count]);

    const checkInfo: ISelectionsCheckInfo<string> = useSelections(
        displayList.map((item) => _get(item, checkKey) as string),
    );

    const tableColumns = useMemo(() => {
        const base = getColumns(columns, { checkInfo, checkKey }) as Record<string, unknown>[];
        if (isLoading && hasCompletedInitialLoad) {
            return wrapAntColumnsForSkeletonRows(base, {
                showStripes: skeletonInfo?.showStripes ?? true,
                cellHeight: skeletonInfo?.cellHeight ?? 20,
                rowHeight: skeletonInfo?.rowHeight ?? 48,
            });
        }
        return base;
    }, [
        columns,
        checkInfo,
        checkKey,
        isLoading,
        hasCompletedInitialLoad,
        skeletonInfo?.showStripes,
        skeletonInfo?.cellHeight,
        skeletonInfo?.rowHeight,
    ]);
    const isHoverIng = useHover(customAdvancedTableRef);
    const [inViewport] = useInViewport(scrollBottomRef);
    const startX = useRef(0);
    const scrollLeft = useRef(0);
    useScroll(tableRef?.current?.querySelector(tableScrollCls), (scroll) => {
        if (!tableWidth) {
            return false;
        }
        let scrollLeft = scroll?.left || 0;
        // 修正比例计算：表格滚动距离转换为滚动条位置
        scrollLeft = (scrollLeft * scrollWidth) / tableWidth;

        scrollTopRef.current && (scrollTopRef.current.style.left = `${scrollLeft}px`);
        scrollBottomRef.current && (scrollBottomRef.current.style.left = `${scrollLeft}px`);
        return false;
    });
    const { isShowScrollBar, styleScroll } = useMemo(() => {
        const scrollBarWidth = tableWidth > 0 ? (scrollWidth / tableWidth) * scrollWidth : 0;
        return {
            isShowScrollBar: tableWidth > scrollWidth + offsetX && scrollWidth > 0 && !hideTopScroll,
            styleScroll: {
                width: scrollBarWidth,
            },
        };
    }, [tableWidth, scrollWidth, tableWidth, hideTopScroll]);

    useEffect(() => {
        onCheckChange?.(checkInfo.selected);
    }, [checkInfo.selected]);
    useLayoutEffect(() => {
        setIsShowTable(!isLoading || hasCompletedInitialLoad);
        if (size?.width) {
            setTableWidth(size?.width);
        }
    }, [isLoading, hasCompletedInitialLoad, size, columns, bodySize]);
    const onCancelSelectAll = () => {
        checkInfo.unSelectAll();
        onCheckChange?.([]);
    };
    useEffect(() => {
        // 获取customAdvancedTableRef下  line-clamp 的样式元素 并依据 data-clamp 属性设置行数
        const lineClampList = customAdvancedTableRef.current?.querySelectorAll('.line-clamp') || [];
        lineClampList?.forEach((item: any) => {
            const clamp = item?.getAttribute('data-clamp') || 2;
            if (clamp) {
                item.style.webkitLineClamp = clamp;
            }
        });
        onCancelSelectAll();
    }, [list, displayList]);
    /*
        页面渲染完成后遍历 columns 下是否存在 autoWidth 为 true 的列，如果存在则获取 widthClassName 对应的所有元素并获取到最大宽度 将最大宽度设置到对应的列上重新渲染表格
        临时方案： 后续在看是否有更好的解决方案
    */
    useEffect(() => {
        // 始终从 defaultColumns 重新派生，确保外部列定义变更（如排序状态）能及时同步
        const freshCols = defaultColumns.map((col) => ({ ...col }));

        const updateColumnWidths = () => {
            freshCols.forEach((item) => {
                if (item.autoWidth) {
                    const widthClassName = item.widthClassName;
                    const widthList = customAdvancedTableRef.current?.querySelectorAll(
                        `.${widthClassName}`,
                    ) as unknown as HTMLElement[];
                    let maxWidth = 0;
                    widthList?.forEach((widthItem: HTMLElement) => {
                        const width = widthItem?.offsetWidth || 0;
                        if (width > maxWidth) {
                            maxWidth = width;
                        }
                    });
                    item.width = maxWidth;
                }
            });
        };

        if (isShowTable) {
            updateColumnWidths();
        }
        setColumns(freshCols);
    }, [defaultColumns, size, isShowTable]);
    // 获取表格信息的函数
    const getTableInfo = useCallback(() => {
        const tableInfo = tableRef?.current?.querySelector(tableScrollCls) as HTMLElement;
        if (!tableInfo) {
            return null;
        }

        const offsetLeft = tableInfo?.offsetLeft || 0;
        const tableScrollWidth = tableInfo.scrollWidth;
        const tableClientWidth = tableInfo.clientWidth;
        const maxScrollLeft = tableScrollWidth - tableClientWidth;
        const currentScrollLeft = tableInfo.scrollLeft;

        return {
            tableInfo,
            offsetLeft,
            tableScrollWidth,
            tableClientWidth,
            maxScrollLeft,
            currentScrollLeft,
        };
    }, [tableScrollCls]); // 移除tableWidth和scrollWidth依赖，使用实时获取的值

    const handleMouseDown = (e: React.MouseEvent) => {
        isDragging.current = true;
        e.preventDefault();

        const tableInfoData = getTableInfo();
        if (tableInfoData) {
            const { offsetLeft, currentScrollLeft } = tableInfoData;

            startX.current = e.pageX - offsetLeft;
            scrollLeft.current = currentScrollLeft;
        }
    };

    const handleMouseUp = useCallback(() => {
        isDragging.current = false;
    }, []);

    // 计算滚动位置的函数
    const calculateScrollPosition = useCallback(
        (mouseX: number, startXValue: number, initialScrollLeft: number, tableInfo: HTMLElement) => {
            // 获取实时的表格宽度，避免使用可能为0的tableWidth
            const currentTableWidth = tableInfo.scrollWidth;
            const currentScrollWidth = scrollWidth;

            if (currentTableWidth <= 0 || currentScrollWidth <= 0) {
                return initialScrollLeft;
            }

            const offsetLeft = tableInfo?.offsetLeft || 0;
            const x = mouseX - offsetLeft;
            const walk = x - startXValue; // 计算鼠标移动的距离

            // 应用正确的比例计算 - 滚动条移动距离与表格滚动距离的比例
            const scrollRatio = currentTableWidth / currentScrollWidth;
            const actualScrollDistance = walk * scrollRatio;

            // 边界检查：确保滚动位置在有效范围内
            const maxScrollLeft = tableInfo.scrollWidth - tableInfo.clientWidth;
            const newScrollLeft = Math.max(0, Math.min(maxScrollLeft, initialScrollLeft + actualScrollDistance));

            return newScrollLeft;
        },
        [scrollWidth], // 移除tableWidth依赖，使用实时获取的值
    );

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (!isDragging.current) {
                return;
            }
            // 阻止默认事件 避免文字选中
            e.preventDefault();

            const tableInfo = tableRef?.current?.querySelector(tableScrollCls) as HTMLElement;
            if (tableInfo) {
                // 验证表格状态

                const newScrollLeft = calculateScrollPosition(e.pageX, startX.current, scrollLeft.current, tableInfo);
                tableInfo.scrollLeft = newScrollLeft;
            }
        },
        [tableScrollCls, calculateScrollPosition],
    );
    const onScrollClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (isDragging.current) {
            return;
        }
        handleMouseMove(e.nativeEvent);
    };
    useEffect(() => {
        // 监听鼠标抬起事件
        // 监听鼠标移动事件
        // 监听鼠标按下事件
        $document.addEventListener('mouseup', handleMouseUp);
        $document.addEventListener('mousemove', handleMouseMove);
        return () => {
            $document.removeEventListener('mouseup', handleMouseUp);
            $document.removeEventListener('mousemove', handleMouseMove);
        };
    }, []);

    const showPaginationBar =
        !!pageInfo &&
        pageInfo.list_total_num > 0 &&
        (pageInfo.total_page_num > 1 || !!advancedPagination?.showTotal || !!advancedPagination?.showPageSizeSelector);
    const ap = advancedPagination;
    const totalForBar = pageInfo?.list_total_num ?? pageInfo?.total_num ?? 0;
    const visibility = isShowScrollBar && (isHoverIng || isDragging.current) && !inViewport;
    return (
        <div
            ref={customAdvancedTableRef}
            className={`${className} custom-advanced-table custom-advanced-table-${sticky ? 'sticky' : 'no-sticky'}`}
            style={style}
            onMouseUp={handleMouseUp}
            onMouseMove={(e) => handleMouseMove(e.nativeEvent)}
        >
            <IfShow show={isLoading && !hasCompletedInitialLoad}>
                <TableSkeleton columns={columns.length} count={8} showStripes {...skeletonInfo} />
            </IfShow>
            <IfShow show={isShowTable}>
                <div {...viewsConfig}>
                    <div className={`scroll-bar-top-info-placeholder ${visibility ? '' : 'hidden'}`}>
                        <div
                            className="scroll-bar-info scroll-bar-top-info"
                            style={{
                                visibility: visibility ? 'visible' : 'hidden'
                            }}
                        >
                            <CustomScrollBar
                                styleScroll={styleScroll}
                                scrollRef={scrollTopRef}
                                onClick={onScrollClick}
                                onMouseDown={handleMouseDown}
                            />
                        </div>
                    </div>
                    <div {...viewsConfig} ref={tableRef}>
                        <Table
                            className="custom-advanced-table-zone"
                            rowClassName={(record, index) => {
                                const rowCls = rowClassName?.(record, index) || '';
                                const zebra = index % 2 === 0 ? 'table-row-even' : 'table-row-odd';
                                if (isCustomAdvancedTableSkeletonRecord(record)) {
                                    return `${rowCls} ${zebra} custom-advanced-table-skeleton-row`.trim();
                                }
                                return `${rowCls} ${zebra}`.trim();
                            }}
                            columns={tableColumns}
                            {...tableConfig}
                            scroll={scroll}
                            pagination={false}
                            dataSource={displayList}
                            locale={{ emptyText: <div className="table-empty-info">{emptyText}</div> }}
                        />
                    </div>
                    <If condition={showPaginationBar}>
                        <div
                            className={`custom-advanced-table-pagination${ap?.showTotal || ap?.showPageSizeSelector
                                ? ' custom-advanced-table-pagination--enhanced'
                                : ''
                                }`}
                        >
                            <PaginationV2
                                showSizeChanger
                                currentPage={pageInfo?.current_page || 1}
                                maxPage={Math.max(1, pageInfo?.total_page_num || 1)}
                                onPageChange={onPageChange}
                                forceShow={!!ap?.showTotal || !!ap?.showPageSizeSelector}
                                showTotal={!!ap?.showTotal}
                                totalCount={totalForBar}
                                formatTotal={ap?.formatTotal}
                                showPageSizeSelector={!!ap?.showPageSizeSelector && !!onPageSizeChange}
                                pageSize={pageInfo?.size}
                                pageSizeOptions={ap?.pageSizeOptions}
                                onPageSizeChange={onPageSizeChange}
                                showFirstLastPage={ap?.showFirstLastPage !== false}
                                ellipsisPagePicker={ap?.ellipsisPagePicker !== false}
                            />
                        </div>
                    </If>
                </div>
            </IfShow>
        </div>
    );
};
export { CustomAdvancedTable };
